/**
 * §7.7 — the export envelope, and §7.7's import rules, as pure functions over
 * plain data. The transaction that commits an import lives in `importer.ts`;
 * everything decided here is decided without a database, so it can be tested
 * against the compositions §13.3 names.
 */

import { HUNDREDTHS_SCALE, RANGE, SCHEMA_VERSION, MAX_NAME_LENGTH, VESSEL_RATIO_MAX } from '../config.js';
import { UNKNOWN_INSULIN } from '../core/insulin.js';
import { INSULINS } from '../data/insulins.js';
import { isTombstone } from '../core/types.js';
import type { Injection, LogRow, Reading, RoundingMode, Settings, Tombstone } from '../core/types.js';
import type { SettingsPeriod } from '../core/periods.js';

export interface ExportedSettingsHistory {
  readonly revision: number;
  readonly changedAtMs: number;
  readonly target: number;
  readonly isf: number;
  readonly icr: number;
  /** §5's rounding mode. Renamed from `mode` on 2026-09-21. */
  readonly roundingMode: RoundingMode;
  /** §8.5 — the insulin in force for this period. `''` when none was recorded. */
  readonly bolusId: string;
}

/**
 * §8.5 — an insulin id crossing the trust boundary.
 *
 * Checked against the TABLE rather than merely typed as a string, because an
 * id is a key and an unrecognised key is not a lesser version of a real one.
 * Anything else becomes `''` — "not recorded" — which every reader of the field
 * already handles, rather than a foreign value that would degrade differently
 * in each of them.
 *
 * `UNKNOWN_INSULIN` passes: it is a deliberate answer with its own meaning, and
 * an import that flattened it to `''` would turn "I don't know" back into a
 * question the reader has already answered.
 */
function readInsulinId(value: unknown): string {
  if (typeof value !== 'string') return '';
  if (value === UNKNOWN_INSULIN) return value;
  return INSULINS.some((row) => row.id === value) ? value : '';
}

/**
 * The settings block AS WRITTEN TO A FILE, declared independently of `Settings`
 * rather than derived from it.
 *
 * It used to be `Omit<Settings, 'revision'>`, which made the file format track
 * the domain type — rename a field and every export silently changes shape,
 * with nothing failing until somebody tried to import an old file. That is the
 * lesson rather than the rename: **a serialisation format that is an `Omit<>`
 * of a live type is a format that changes when the type does.**
 * `ExportedSettingsHistory` was already written this way; this one was not, and
 * that asymmetry was the bug.
 *
 * The 2026-09-21 rename DID reach the file, deliberately — Momin's ruling, on
 * the grounds that nobody is using the app yet, so a name people can read is
 * worth more than compatibility with files that do not exist. `parseEnvelope`
 * accepted the old spelling for a day; that went too.
 */
export interface ExportedSettings {
  readonly target: number;
  readonly isf: number;
  readonly icr: number;
  readonly roundingMode: RoundingMode;
  readonly threshold: number;
  readonly basalName: string;
  readonly basalUnits: number;
  readonly basalTiming: string;
  readonly bolusId: string;
  readonly eatDelayMinutes: number | null;
  readonly personName: string;
}

export interface Envelope {
  readonly schemaVersion: number;
  /**
   * §7.7 v14 — present and EXPLICITLY EMPTY when no settings have ever been
   * committed. v13's composed test exports from exactly that state and §7.7
   * specified the block unconditionally without saying what it holds, leaving
   * the test to a judgement call.
   */
  readonly settings: ExportedSettings | Record<string, never>;
  /**
   * §6.7 — present ONLY when the state is `answered`. Absent for `unanswered`
   * and `declined`, and the STATE ITSELF is never exported: a refusal recorded on
   * one install should not silence a fresh one, and the cost is one re-ask after
   * a storage loss.
   */
  readonly dosingHistoryBeforeApp?: { readonly answeredAtMs: number; readonly text: string };
  /** §7.7 — read-only provenance. Never adopted, and `threshold` is deliberately absent. */
  readonly settingsHistory: readonly ExportedSettingsHistory[];
  readonly readings: readonly Reading[];
  /**
   * §7.7 v24 — `Injection | Tombstone`, discriminated by the presence of
   * `deleted`. v23 specified the tombstone and not the envelope, so a valid
   * exported tombstone lacked a field the importer required, on exactly the
   * restore path tombstones exist to protect.
   */
  readonly log: readonly LogRow[];
  /**
   * §7.7 T34 — the reader's OWN measured grams per food, added 2026-09-26.
   *
   * It was absent for as long as the feature existed, and three strings said
   * otherwise: §7.7.1's table claims "Contains: everything" and "Restores?
   * Yes", `COPY.exports.lastCopy` offers "a copy you can restore from", and
   * start over's enumeration of what it destroys never mentioned it — while
   * `COPY.clear.exportFirst` offered "Save a copy first" on that very dialog.
   * A save that does not cover what the next button destroys is worse than no
   * save, because the reader stops looking.
   *
   * It is dose-bearing: `tallyGrams` sums `calibration[id]?.grams ?? grams`.
   * Losing it silently restores the table's figure — worth +8.9 units on a
   * sheermal calibrated down to a bakery piece, in the over-dosing direction.
   *
   * OPTIONAL, and `schemaVersion` deliberately does not move. `parseEnvelope`
   * rejects only a HIGHER version, so an absent block reads clean on both
   * sides: today's builds ignore it, newer builds read it. Bumping would make
   * every new file unreadable to any older build, for a field that build would
   * never have written.
   */
  readonly calibration?: Readonly<Record<string, { readonly grams: number; readonly setAt: number }>>;
  /**
   * T31 — the reader's own vessels, as ratios. Added 2026-09-27, and it was
   * missing for the same reason `calibration` was: the feature shipped, the
   * envelope was not revisited, and nothing checked. T34 fixed exactly this
   * defect for the per-food map two phases earlier, which is what makes
   * repeating it worth writing down rather than quietly patching.
   *
   * Dose-bearing: a plate ratio of 1.5 moves `rice-plate` from 84 to 126 g.
   * Losing it on restore silently returns all seven rows to the table's
   * figures, and §7.7.1 claims the JSON restores everything.
   */
  readonly vessels?: Readonly<Record<string, {
    readonly ratio: number;
    readonly emptyGrams: number;
    readonly fullGrams: number;
    readonly tared: 'subtracted' | 'scale';
    readonly sourceFoodId: string;
    readonly referenceGrams: number;
    readonly setAt: number;
  }>>;
}

export interface ExportInput {
  readonly settings: Settings | null;
  readonly calibration: Readonly<Record<string, { readonly grams: number; readonly setAt: number }>>;
  readonly vessels: Readonly<Record<string, {
    readonly ratio: number;
    readonly emptyGrams: number;
    readonly fullGrams: number;
    readonly tared: 'subtracted' | 'scale';
    readonly sourceFoodId: string;
    readonly referenceGrams: number;
    readonly setAt: number;
  }>>;
  readonly settingsHistory: readonly SettingsPeriod[];
  readonly log: readonly LogRow[];
  readonly readings: readonly Reading[];
  readonly dosingHistory: { readonly state: string; readonly text: string; readonly answeredAtMs: number | null };
}

export function buildEnvelope(input: ExportInput): Envelope {
  const settings: Envelope['settings'] =
    input.settings === null
      ? {}
      : {
          target: input.settings.target,
          isf: input.settings.isf,
          icr: input.settings.icr,
          roundingMode: input.settings.roundingMode,
          threshold: input.settings.threshold,
          basalName: input.settings.basalName,
          basalUnits: input.settings.basalUnits,
          basalTiming: input.settings.basalTiming,
          bolusId: input.settings.bolusId,
          eatDelayMinutes: input.settings.eatDelayMinutes,
          personName: input.settings.personName,
        };

  const envelope: Envelope = {
    schemaVersion: SCHEMA_VERSION,
    settings,
    // `imported` never travels: it is LOCAL provenance, and a file cannot say
    // whether a prescription was ever in force on the install reading it.
    settingsHistory: input.settingsHistory.map((period) => ({
      revision: period.revision,
      changedAtMs: period.changedAtMs,
      target: period.target,
      isf: period.isf,
      icr: period.icr,
      roundingMode: period.roundingMode,
      bolusId: period.bolusId,
    })),
    readings: input.readings,
    log: input.log,
  };

  // Omitted when empty rather than written as `{}`, so a reader who never
  // calibrated anything exports a file byte-identical to what they got before
  // T34 — which is what makes this change invisible to everyone it does not
  // affect.
  const calibration = Object.fromEntries(
    Object.entries(input.calibration).filter(([, row]) => readCalibrationEntry(row) !== null),
  );
  const withCalibration: Envelope =
    Object.keys(calibration).length === 0 ? envelope : { ...envelope, calibration };
  const vessels = Object.fromEntries(
    Object.entries(input.vessels).filter(([, row]) => readVesselEntry(row) !== null),
  );
  const withVessels: Envelope =
    Object.keys(vessels).length === 0 ? withCalibration : { ...withCalibration, vessels };

  if (input.dosingHistory.state === 'answered' && input.dosingHistory.answeredAtMs !== null) {
    return {
      ...withVessels,
      dosingHistoryBeforeApp: {
        answeredAtMs: input.dosingHistory.answeredAtMs,
        text: input.dosingHistory.text,
      },
    };
  }
  return withVessels;
}

// ─── import ─────────────────────────────────────────────────────────────────

export type ImportProblem =
  | { readonly kind: 'not_an_object' }
  | { readonly kind: 'future_schema'; readonly found: unknown }
  | { readonly kind: 'bad_shape'; readonly field: string };

export type ParsedEnvelope =
  | { readonly ok: true; readonly envelope: Envelope }
  | { readonly ok: false; readonly problem: ImportProblem };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function finiteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function inHardRange(value: unknown, field: keyof typeof RANGE): value is number {
  if (!finiteNumber(value)) return false;
  const [lo, hi] = RANGE[field].hard;
  return value >= lo && value <= hi;
}

const MODES: readonly RoundingMode[] = ['nearest', 'half', 'ceil', 'floor', 'off'];

/**
 * §2.2 — the stored insulin fields hold integer HUNDREDTHS, while `RANGE` is
 * denominated in UNITS. That mismatch is note 3's naming trap (`injectedUnits`
 * HOLDS hundredths), and it is why this helper exists instead of a bare
 * `inHardRange` at the call site: a check that forgot the conversion would
 * read a mis-scaled 25 units as 25 hundredths — and reject every legitimate
 * row as 1100 "units". The integer check is §11.3's "precision": a fractional
 * hundredth throws in `formatHundredths` and takes the history screen and the
 * readable export down with it.
 */
function injectedHundredthsInRange(value: unknown): value is number {
  if (!finiteNumber(value) || !Number.isInteger(value)) return false;
  return inHardRange(value / HUNDREDTHS_SCALE, 'injected');
}

// §6.4's ceiling with every setting at its hard extreme — the largest dose any
// in-range prescription can produce: (600 − 70) / 5 + 300 / 1 = 406 units.
// The bound for a CALCULATED figure has to be absolute rather than
// per-prescription, because by import time the settings that produced the row
// may not be in the file at all (§7.7 remaps history; §1.2 re-enters).
const [, ABS_MAX_BLOOD_SUGAR] = RANGE.bloodSugar.hard;
const [ABS_MIN_TARGET] = RANGE.target.hard;
const [ABS_MIN_ISF] = RANGE.isf.hard;
const [ABS_MIN_ICR] = RANGE.icr.hard;
const [, ABS_MAX_CARBS] = RANGE.carbs.hard;
const MAX_CALCULATED_HUNDREDTHS =
  ((ABS_MAX_BLOOD_SUGAR - ABS_MIN_TARGET) / ABS_MIN_ISF + ABS_MAX_CARBS / ABS_MIN_ICR) *
  HUNDREDTHS_SCALE;

function readInjection(value: Record<string, unknown>): Injection | null {
  // §11.3 — RE-VALIDATE ON EVERY LOAD AND IMPORT: type, presence, finiteness,
  // precision, range. A row that fails is dropped rather than repaired, because
  // repairing it would invent a dose.
  const { id, timestamp, bloodSugar, carbs, injectedUnits, settingsRevision } = value;
  // `calculatedUnits`, never `units`. The short name sat next to
  // `injectedUnits` for long enough that §11.2 has a paragraph about a reviewer
  // nearly pinning the wrong one, which is the whole reason for the rename.
  const units = value.calculatedUnits;
  if (typeof id !== 'string' || id === '') return null;
  if (!finiteNumber(timestamp)) return null;
  if (bloodSugar !== null && !inHardRange(bloodSugar, 'bloodSugar')) return null;
  if (!inHardRange(carbs, 'carbs')) return null;
  // Both insulin figures are integer hundredths (§2.2). `units` keeps its
  // floor at zero — a zero-unit calculation beside a real injection is a
  // legitimate row — but is capped at §6.4's absolute ceiling; the injected
  // figure gets §7.1's own range, converted (see the helper above).
  if (!finiteNumber(units) || !Number.isInteger(units)) return null;
  if (units < 0 || units > MAX_CALCULATED_HUNDREDTHS) return null;
  if (!injectedHundredthsInRange(injectedUnits)) return null;
  if (!finiteNumber(settingsRevision)) return null;
  const timingAdvice = value.timingAdvice;
  if (timingAdvice !== 'before' && timingAdvice !== 'eat_first' && timingAdvice !== 'suppressed') {
    return null;
  }
  return {
    id,
    timestamp,
    bloodSugar: bloodSugar === null ? null : bloodSugar,
    carbs,
    calculatedUnits: units,
    injectedUnits,
    settingsRevision,
    overrodeStacking: value.overrodeStacking === true,
    timingAdvice,
    advisoryFlagged: value.advisoryFlagged === true,
  };
}

/**
 * §11.3 — *"Re-validate on every LOAD and import — type, presence, finiteness,
 * precision, range."* Exported because until 2026-09-11 only the IMPORT half of
 * that sentence had an implementation: `readAll` returned whatever the store
 * held, so a row written by an older build, or corrupted in place, reached
 * §7.4's gate and the history screen unchecked — and a fractional
 * `injectedUnits` threw in `formatHundredths`, taking down the history screen
 * and the readable export from one bad row, on exactly the restore path §11.3
 * promises validate-before-commit protection for.
 *
 * One function for both paths deliberately: two validators that are supposed to
 * agree are two validators that will eventually disagree.
 */
/**
 * One food's calibrated grams, or null.
 *
 * §11.3 says re-validate on every load AND every import, and until T34 this map
 * had NO validator on either path: `readAll` returned it unchecked and the
 * input field tested `Number.isFinite` and nothing else — no range, no sign.
 * So the import path and the load path are both fixed by this one function,
 * for the reason `readLogRow` states above: two validators meant to agree are
 * two validators that will eventually disagree.
 *
 * A row that fails is DROPPED rather than repaired, on the same rule — a
 * repaired calibration would invent a figure the reader never measured, which
 * is worse than falling back to the table's, because the app would then claim
 * "Yours" over a number that is not theirs.
 *
 * `RANGE.carbs.hard` bounds it because that is the same bound the carbohydrate
 * field enforces; a calibration outside it could never have been typed.
 */
export function readCalibrationEntry(
  value: unknown,
): { readonly grams: number; readonly setAt: number } | null {
  if (!isRecord(value)) return null;
  if (!finiteNumber(value.grams) || !finiteNumber(value.setAt)) return null;
  const [low, high] = RANGE.carbs.hard;
  if (value.grams < low || value.grams > high) return null;
  return { grams: value.grams, setAt: value.setAt };
}

/**
 * One vessel's stored ratio, or null.
 *
 * Same drop-don't-repair rule as `readCalibrationEntry`: a repaired ratio would
 * invent a plate nobody weighed. Bounded because an unbounded one multiplies
 * every plate row — a ratio of 10 turns `rice-plate` into 840 g.
 */
export function readVesselEntry(value: unknown): Readonly<{
  ratio: number; emptyGrams: number; fullGrams: number;
  tared: 'subtracted' | 'scale'; sourceFoodId: string;
  referenceGrams: number; setAt: number;
}> | null {
  if (!isRecord(value)) return null;
  if (!finiteNumber(value.ratio) || !finiteNumber(value.setAt)) return null;
  if (value.ratio <= 0 || value.ratio > VESSEL_RATIO_MAX) return null;
  if (!finiteNumber(value.emptyGrams) || !finiteNumber(value.fullGrams)) return null;
  if (!finiteNumber(value.referenceGrams) || value.referenceGrams <= 0) return null;
  if (value.tared !== 'subtracted' && value.tared !== 'scale') return null;
  if (typeof value.sourceFoodId !== 'string') return null;
  // The working is carried so a restored row stays auditable — a ratio with no
  // weighings behind it is the provenance loss §7.7 exists to prevent.
  return {
    ratio: value.ratio, emptyGrams: value.emptyGrams, fullGrams: value.fullGrams,
    tared: value.tared, sourceFoodId: value.sourceFoodId,
    referenceGrams: value.referenceGrams, setAt: value.setAt,
  };
}

export function readLogRow(value: unknown): LogRow | null {
  if (!isRecord(value)) return null;
  return value.deleted === true ? readTombstone(value) : readInjection(value);
}

function readTombstone(value: Record<string, unknown>): Tombstone | null {
  // §7.7 v24 — a tombstone is NOT validated against the prescription. "It
  // asserts an absence; there is nothing to range-check."
  const { id, timestamp, deletedAtMs } = value;
  if (typeof id !== 'string' || id === '') return null;
  if (!finiteNumber(timestamp) || !finiteNumber(deletedAtMs)) return null;
  return { id, timestamp, deleted: true, deletedAtMs };
}

function readReading(value: Record<string, unknown>): Reading | null {
  const { id, timestamp, bloodSugar, note } = value;
  if (typeof id !== 'string' || id === '') return null;
  if (!finiteNumber(timestamp)) return null;
  if (!inHardRange(bloodSugar, 'bloodSugar')) return null;
  // §7.8 — the note is one line FROM A FIXED LIST, which is why §7.7.1 can say
  // it is "already safe" for the readable export. An unrecognised value is
  // dropped rather than carried: a free-text note arriving through the import
  // path would silently make that sentence false.
  const notes: readonly Reading['note'][] = ['before_bed', 'overnight', 'felt_low', 'after_exercise'];
  const known = notes.find((candidate) => candidate === note);
  return known === undefined
    ? { id, timestamp, bloodSugar }
    : { id, timestamp, bloodSugar, note: known };
}

/**
 * §11.3 — import is VALIDATE BEFORE COMMIT. It rejects future schemas and
 * preserves existing data on failure, so a half-read file can never leave the
 * store in a state neither the old data nor the new one describes.
 */
export function parseEnvelope(raw: unknown): ParsedEnvelope {
  if (!isRecord(raw)) return { ok: false, problem: { kind: 'not_an_object' } };

  const schemaVersion = raw.schemaVersion;
  if (!Number.isInteger(schemaVersion)) {
    return { ok: false, problem: { kind: 'bad_shape', field: 'schemaVersion' } };
  }
  if ((schemaVersion as number) > SCHEMA_VERSION) {
    return { ok: false, problem: { kind: 'future_schema', found: schemaVersion } };
  }

  const settingsRaw = raw.settings;
  if (!isRecord(settingsRaw)) return { ok: false, problem: { kind: 'bad_shape', field: 'settings' } };
  const settings: Envelope['settings'] =
    Object.keys(settingsRaw).length === 0
      ? {}
      : (() => {
          const mode = settingsRaw.roundingMode;
          if (
            !inHardRange(settingsRaw.target, 'target') ||
            !inHardRange(settingsRaw.isf, 'isf') ||
            !inHardRange(settingsRaw.icr, 'icr') ||
            !inHardRange(settingsRaw.threshold, 'threshold') ||
            typeof mode !== 'string' ||
            !MODES.includes(mode as RoundingMode)
          ) {
            return {};
          }
          return {
            target: settingsRaw.target,
            isf: settingsRaw.isf,
            icr: settingsRaw.icr,
            roundingMode: mode as RoundingMode,
            threshold: settingsRaw.threshold,
            basalName: typeof settingsRaw.basalName === 'string' ? settingsRaw.basalName : '',
            basalUnits: inHardRange(settingsRaw.basalUnits, 'basalUnits') ? settingsRaw.basalUnits : 0,
            basalTiming: typeof settingsRaw.basalTiming === 'string' ? settingsRaw.basalTiming : '',
            bolusId: readInsulinId(settingsRaw.bolusId),
            // §8.5 — the reader's own wait, range-checked like every other
            // number from a file. Anything outside the field's own bounds
            // becomes null, which is "the class range stands" and not a
            // silently clamped instruction about when to eat.
            eatDelayMinutes: inHardRange(settingsRaw.eatDelayMinutes, 'eatDelay')
              ? settingsRaw.eatDelayMinutes
              : null,
            // Validated as a STRING and nothing more. It is untrusted text from
            // a file, it enters no calculation, and the readable export escapes
            // it — §7.7.1's escaping rule is what makes that safe. A file
            // that simply has no name here is not an error: this is arbitrary
            // JSON, and every field has to survive being absent.
            personName:
              typeof settingsRaw.personName === 'string'
                ? settingsRaw.personName.slice(0, MAX_NAME_LENGTH)
                : '',
          };
        })();

  const historyRaw = Array.isArray(raw.settingsHistory) ? raw.settingsHistory : [];
  const settingsHistory: ExportedSettingsHistory[] = [];
  for (const entry of historyRaw) {
    if (!isRecord(entry)) continue;
    const mode = entry.roundingMode;
    if (
      !Number.isInteger(entry.revision) ||
      !finiteNumber(entry.changedAtMs) ||
      !inHardRange(entry.target, 'target') ||
      !inHardRange(entry.isf, 'isf') ||
      !inHardRange(entry.icr, 'icr') ||
      typeof mode !== 'string' ||
      !MODES.includes(mode as RoundingMode)
    ) {
      continue;
    }
    settingsHistory.push({
      revision: entry.revision as number,
      changedAtMs: entry.changedAtMs,
      target: entry.target,
      isf: entry.isf,
      icr: entry.icr,
      roundingMode: mode as RoundingMode,
      bolusId: readInsulinId(entry.bolusId),
    });
  }

  const log: LogRow[] = [];
  for (const entry of Array.isArray(raw.log) ? raw.log : []) {
    const row = readLogRow(entry);
    if (row !== null) log.push(row);
  }

  const readings: Reading[] = [];
  for (const entry of Array.isArray(raw.readings) ? raw.readings : []) {
    if (!isRecord(entry)) continue;
    const row = readReading(entry);
    if (row !== null) readings.push(row);
  }

  // Same drop-don't-repair rule the log and readings use above: a bad entry
  // vanishes, the row falls back to the table's figure, and nothing invents a
  // number the reader never measured.
  const calibration: Record<string, { grams: number; setAt: number }> = {};
  if (isRecord(raw.calibration)) {
    for (const [id, entry] of Object.entries(raw.calibration)) {
      const row = readCalibrationEntry(entry);
      if (row !== null) calibration[id] = row;
    }
  }

  const vessels: Record<string, NonNullable<ReturnType<typeof readVesselEntry>>> = {};
  if (isRecord(raw.vessels)) {
    for (const [id, entry] of Object.entries(raw.vessels)) {
      const row = readVesselEntry(entry);
      if (row !== null) vessels[id] = row;
    }
  }

  const base: Envelope = {
    schemaVersion: schemaVersion as number,
    settings,
    settingsHistory,
    readings,
    log,
  };
  const withCal: Envelope =
    Object.keys(calibration).length === 0 ? base : { ...base, calibration };
  const envelope: Envelope =
    Object.keys(vessels).length === 0 ? withCal : { ...withCal, vessels };

  const dosing = raw.dosingHistoryBeforeApp;
  if (isRecord(dosing) && finiteNumber(dosing.answeredAtMs) && typeof dosing.text === 'string') {
    return {
      ok: true,
      envelope: {
        ...envelope,
        dosingHistoryBeforeApp: { answeredAtMs: dosing.answeredAtMs, text: dosing.text },
      },
    };
  }
  return { ok: true, envelope };
}

export interface MergePlan {
  readonly log: readonly LogRow[];
  readonly readings: readonly Reading[];
  readonly historyToAppend: readonly (ExportedSettingsHistory & { readonly imported: true })[];
  /** old imported revision -> the local id it was rewritten to */
  readonly revisionRemap: ReadonlyMap<number, number>;
}

/**
 * §7.7 — **imported revisions are REMAPPED, never merged by number.**
 *
 * `settingsRevision` is a bare monotonic integer with no identity across
 * installs, and the plan's own recovery flow breaks it: §1.2 says storage loss
 * requires re-entering the settings, so the user re-enters them (producing local
 * revisions 1..key) and THEN imports the backup (carrying an unrelated 1..n).
 * Merging by number collides and attributes rows to settings that never produced
 * them — the exact falsity this mechanism was added to remove, now in front of
 * the prescriber. Discarding the history instead leaves imported rows pointing
 * at nothing.
 *
 * Import into an EMPTY store keeps the imported numbers — the complement of the
 * rule, stated in v11 rather than left implicit.
 */
export function planMerge(
  envelope: Envelope,
  local: {
    readonly log: readonly LogRow[];
    readonly readings: readonly Reading[];
    readonly maxHistoryRevision: number | null;
  },
): MergePlan {
  const remap = new Map<number, number>();
  const historyToAppend: (ExportedSettingsHistory & { imported: true })[] = [];

  const ordered = [...envelope.settingsHistory].sort((a, b) => a.revision - b.revision);
  if (local.maxHistoryRevision === null) {
    // Empty store: the imported numbers stand as they are.
    for (const entry of ordered) {
      remap.set(entry.revision, entry.revision);
      historyToAppend.push({ ...entry, imported: true });
    }
  } else {
    let next = local.maxHistoryRevision + 1;
    for (const entry of ordered) {
      remap.set(entry.revision, next);
      historyToAppend.push({ ...entry, revision: next, imported: true });
      next += 1;
    }
  }

  // §7.7 — merged by `id`. §7.3 — **a tombstone wins over a live row with the
  // same `id` in EITHER direction.** Deletion is the later statement of intent,
  // and without a tiebreak the outcome would depend on file order. This is also
  // the only reason a tombstone is exported at all: without it, import's merge
  // sees an `id` it does not have and adds the dose back.
  const byId = new Map<string, LogRow>();
  const consider = (row: LogRow, remapRevision: boolean): void => {
    const existing = byId.get(row.id);
    if (existing !== undefined && isTombstone(existing)) return;
    if (existing !== undefined && !isTombstone(row)) return;
    if (isTombstone(row)) {
      byId.set(row.id, row);
      return;
    }
    const mapped = remapRevision ? (remap.get(row.settingsRevision) ?? row.settingsRevision) : row.settingsRevision;
    byId.set(row.id, { ...row, settingsRevision: mapped });
  };
  for (const row of local.log) consider(row, false);
  for (const row of envelope.log) consider(row, true);

  const readingsById = new Map<string, Reading>();
  for (const reading of local.readings) readingsById.set(reading.id, reading);
  for (const reading of envelope.readings) {
    if (!readingsById.has(reading.id)) readingsById.set(reading.id, reading);
  }

  return {
    log: [...byId.values()],
    readings: [...readingsById.values()],
    historyToAppend,
    revisionRemap: remap,
  };
}
